'use client'
import { Pagination, Table, TableRow, TableHeader, TableCell, SmartLink, MoreButton } from "@/components/ui";
import { ArrowUp, Pen, Trash } from "@/assets/icons";
import { CommentSearchParams, StatusFilterParams, FeedbacksResponse } from "@/types";
import { ColorButton } from "@/components/ui";
import React, { useEffect, useState } from "react";
import { listFeedbacks } from "@/app/api";

export function ManageFeedbacks() {
    const [feedbacksData, setFeedbacksData] = useState<FeedbacksResponse>();
    const [expandedComments, setExpandedComments] = useState<Record<string, boolean>>({});

    const [filters, setFilters] = useState<CommentSearchParams>({
        page: 1,
        perPage: 10,
        order: 'asc',
        orderBy: 'createdAt',
        status: 'pending',
    });

    useEffect(() => {
        const fetchFeedbacks = async () => {
            try {
                const data = await listFeedbacks(filters)
                setFeedbacksData(data)
            } catch (err) {
                console.log("Erro ao carregar feedbacks: ", err);
            }
        }

        fetchFeedbacks()
    }, [filters])

    const handleStatusChange = (status: StatusFilterParams) => {
        setFilters({ ...filters, status, page: 1 })
    };

    const handlePageChange = (page: number) => {
        setFilters({ ...filters, page });
    };

    const toggleExpand = (commentId: string) => {
        setExpandedComments(prev => ({
            ...prev,
            [commentId]: !prev[commentId]
        }));
    };

    const truncateText = (text: string, length: number) => {
        return text.length > length ? text.substring(0, length) + '...' : text;
    };

    const feedbacks = feedbacksData?.data || []

    return (
        <div className="pt-8">
            <div className="flex justify-start">
                <div className="flex flex-wrap gap-4 justify-center items-center">

                    <ColorButton disabled={filters.status === 'pending'}
                        onClick={() => { handleStatusChange('pending') }}>
                        Pendentes
                    </ColorButton>
                    <ColorButton disabled={filters.status === 'approved'}
                        onClick={() => { handleStatusChange('approved') }}>
                        Aprovados
                    </ColorButton>
                    <ColorButton disabled={filters.status === 'rejected'}
                        onClick={() => { handleStatusChange('rejected') }}>
                        Reprovados
                    </ColorButton>
                    <ColorButton disabled={filters.status === 'flagged'}
                        onClick={() => { handleStatusChange('flagged') }}>
                        Sinalizados
                    </ColorButton>

                </div>
            </div>

            <div className="p-8">
                <div className="hidden md:flex">
                    <Table>
                        <thead>
                            <TableRow>
                                <TableHeader>Sequência</TableHeader>
                                <TableHeader>Conteúdo</TableHeader>
                                <TableHeader>Autor</TableHeader>
                                <TableHeader>Email do autor</TableHeader>
                                <TableHeader>Status</TableHeader>
                                <TableHeader>Data do envio</TableHeader>
                                <TableHeader><ArrowUp /></TableHeader>
                            </TableRow>
                        </thead>
                        <tbody>
                            {feedbacks.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center">
                                        Não existem retornos desse status.
                                    </TableCell>
                                </TableRow>
                            ) : (feedbacks.map((feedback) => (
                                <React.Fragment key={feedback.id}>
                                    <TableRow>
                                        <TableCell>{feedback.sequence}</TableCell>
                                        <TableCell>
                                            {expandedComments[feedback.id]
                                                ? feedback.content
                                                : truncateText(feedback.content, 100)}
                                        </TableCell>

                                        <TableCell>
                                            {feedback.user?.name ? 'Usuário' : 'Visitante'} - {feedback.user?.name || feedback.visitor?.name}
                                        </TableCell>

                                        <TableCell>
                                            {feedback.user?.email || feedback.visitor?.email || 'Não informado'}
                                        </TableCell>

                                        <TableCell>
                                            {feedback.status === 'PENDING' ? 'Pendente'
                                                : feedback.status === 'APPROVED' ? "Aprovado"
                                                    : feedback.status === 'REJECTED' ? 'Rejeitado'
                                                        : feedback.status === 'FLAGGED' ? 'Sinalizado'
                                                            : 'Pendente'
                                            }
                                        </TableCell>

                                        <TableCell>{new Date(feedback.createdAt).toLocaleDateString()}</TableCell>
                                        <TableCell>
                                            <MoreButton onClick={() => toggleExpand(feedback.id)} />
                                        </TableCell>
                                    </TableRow>
                                    {expandedComments[feedback.id]
                                        ? <TableRow>
                                            <TableHeader>Observação</TableHeader>
                                            <TableCell colSpan={4}>{feedback.observation}</TableCell>
                                            <TableCell colSpan={2} className="flex gap-10">
                                                <SmartLink type={"internal"} route={"adminCommentsFeedbackIdUpdate"}
                                                    params={[`${feedback.id}`]}>
                                                    <Pen />
                                                </SmartLink>
                                                <SmartLink type={"internal"} route={"adminCommentsFeedbackIdDelete"}
                                                    params={[`${feedback.id}`]}>
                                                    <Trash />
                                                </SmartLink>
                                            </TableCell>
                                        </TableRow>
                                        : ''}
                                </React.Fragment>
                            )))
                            }
                        </tbody>
                    </Table>
                </div>

                <div className="flex md:hidden">
                    <Table>
                        <tbody>
                            {feedbacks.length === 0 ? (
                                <TableRow>
                                    <TableCell className="text-center">
                                        Não existem retornos desse status.
                                    </TableCell>
                                </TableRow>
                            ) : (feedbacks.map((feedback) => (
                                <TableRow key={feedback.id} >
                                    <TableHeader>Conteúdo</TableHeader>
                                    <TableCell>{feedback.content}</TableCell>

                                    <TableHeader>Autor</TableHeader>
                                    <TableCell>
                                        {feedback.user?.name ? 'Usuário' : 'Visitante'} - {feedback.user?.name || feedback.visitor?.name}
                                    </TableCell>

                                    <TableHeader>Email do autor</TableHeader>
                                    <TableCell>{feedback.user?.email || feedback.visitor?.email || 'Não informado'}</TableCell>

                                    <TableHeader>Status</TableHeader>
                                    <TableCell>
                                        {feedback.status === 'PENDING' ? 'Pendente'
                                            : feedback.status === 'APPROVED' ? "Aprovado"
                                                : feedback.status === 'REJECTED' ? 'Rejeitado'
                                                    : feedback.status === 'FLAGGED' ? 'Sinalizado'
                                                        : 'Pendente'
                                        }
                                    </TableCell>
                                    <TableHeader>Data do envio</TableHeader>
                                    <TableCell>
                                        {new Date(feedback.createdAt).toLocaleDateString()}
                                    </TableCell>
                                    <TableHeader>Observação</TableHeader>
                                    <TableCell>{feedback.observation}</TableCell>
                                    <TableCell className="flex gap-10">
                                        <SmartLink type={"internal"} route={"adminCommentsFeedbackIdUpdate"}
                                            params={[`${feedback.id}`]}>
                                            <Pen />
                                        </SmartLink>
                                        <SmartLink type={"internal"} route={"adminCommentsFeedbackIdDelete"}
                                            params={[`${feedback.id}`]}>
                                            <Trash />
                                        </SmartLink>
                                    </TableCell>
                                    <TableCell className="border-t-2 min-w-56 border-ochre_accent" />
                                </TableRow>
                            ))
                            )
                            }
                        </tbody>
                    </Table>
                </div>
            </div>

            <div>
                {feedbacksData?.pagination && (
                    <Pagination
                        currentPage={feedbacksData.pagination.page}
                        totalPages={Math.ceil(feedbacksData.pagination.total / feedbacksData.pagination.perPage)}
                        onPageChange={handlePageChange}
                    />)}
            </div>

        </div>
    )
}
